import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { ArticleHistoryResponse, KnowledgeBase, KnowledgeBaseResponse, SingleKnowledgeBaseResponse, ListArticlesResponse, ArticleListItem, ArticleState, ArticleUploadResponse, ArticleImportJobInitResponse, ArticleImportJobStatusResponse, ImportJobsListResponse, ImportJobDownloadResponse, BatchCreateDraftsResponse, BatchDeleteImmediateResponse, RetrievalResponse } from '../models/knowledge-base.model';
import { ListBedrockSynchronizationsResponse, GetBedrockSynchronizationResponse, GetArticleWithTrackResponse } from '../models/knowledge-base-sync.model';
import { MOCK_KNOWLEDGE_BASES } from '../utils/constants';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: "root",
})
export class KnowledgeBaseService {
    private readonly http = inject(HttpClient);
    private readonly authService = inject(AuthService);

    private readonly baseUrl = environment.userManagementApiUrl;
    private readonly knowledgeBaseEndpoint = `${this.baseUrl}/knowledge-base`;
    private currentUsername: string = '';

    constructor() {
        // Suscribirse al usuario autenticado para obtener el email
        this.authService.user$.subscribe(user => {
        if (user && user.email) {
            this.currentUsername = user.email;
        }
        });
    }

    // Obtener los artículos de una base de conocimiento específica
    getArticlesByKnowledgeBaseId(
        knowledgeBaseId: number, 
        state?: string, 
        page: number = 1, 
        pageSize: number = 20,
        filters?: { [key: string]: number[] | string },
        isActive: boolean = true
    ): Observable<ListArticlesResponse> {
        if (environment.mockAuth) {
            return this.getMockArticlesResponse(knowledgeBaseId, state, page, pageSize);
        }

        const headers = this.authService.getAuthHeaders();
        const url = this.buildArticlesUrl(knowledgeBaseId, state, page, pageSize, filters, isActive);
        
        return this.http.get<ListArticlesResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error fetching articles:', error);
                return throwError(() => error);
            })
        );
    }

    // List Bedrock Synchronizations
    listBedrockSynchronizations(
        knowledgeBaseId: number,
        page: number = 1,
        pageSize: number = 20,
        environmentCode?: 'QA' | 'PROD',
        status?: 'SYNCHRONIZING' | 'PUBLISHED' | 'FAILED'
    ): Observable<ListBedrockSynchronizationsResponse> {
        let url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/synchronizations?page=${page}&pageSize=${pageSize}`;
        if (environmentCode) {
            url += `&environment=${environmentCode}`;
        }
        if (status) {
            url += `&status=${status}`;
        }

        if (environment.mockAuth) {
            // Return mock data or handle appropriately if no mock is needed
            return of({
                synchronizations: [],
                pagination: { currentPage: page, pageSize, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false },
                filters: {}
            });
        }

        const headers = this.authService.getAuthHeaders();
        return this.http.get<ListBedrockSynchronizationsResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error fetching bedrock synchronizations:', error);
                return throwError(() => error);
            })
        );
    }

    // Get Bedrock Synchronization Detail
    getBedrockSynchronization(knowledgeBaseId: number, syncId: number): Observable<GetBedrockSynchronizationResponse> {
        const url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/synchronizations/${syncId}`;
        
        if (environment.mockAuth) {
            return of({
                synchronization: {} as any,
                revisions: [],
                count: 0
            });
        }

        const headers = this.authService.getAuthHeaders();
        return this.http.get<GetBedrockSynchronizationResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error fetching bedrock synchronization detail:', error);
                return throwError(() => error);
            })
        );
    }

    // Get Article Version Filtered by Environment and Version
    getArticleWithTrack(
        knowledgeBaseId: number,
        articleId: number,
        envCode: 'DRAFT' | 'QA' | 'PROD',
        version?: number
    ): Observable<GetArticleWithTrackResponse> {
        let url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/articles/${articleId}?environment=${envCode}`;
        if (version !== undefined && envCode !== 'DRAFT') {
            url += `&version=${version}`;
        }

        if (environment.mockAuth) {
            return of({
                article: {} as any,
                track: {} as any
            });
        }

        const headers = this.authService.getAuthHeaders();
        return this.http.get<GetArticleWithTrackResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error fetching article with track:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Construye la URL con parámetros para obtener artículos
     */
    private buildArticlesUrl(
        knowledgeBaseId: number,
        state: string | undefined,
        page: number,
        pageSize: number,
        filters?: { [key: string]: number[] | string },
        isActive: boolean = true
    ): string {
        let url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/articles?page=${page}&pageSize=${pageSize}&is_active=${isActive}`;
        if (state) {
            url += `&status=${state}`;
        }
        
        if (filters) {
            url = this.appendFiltersToUrl(url, filters);
        }
        
        return url;
    }

    /**
     * Agrega filtros a la URL
     */
    private appendFiltersToUrl(url: string, filters: { [key: string]: number[] | string }): string {
        Object.keys(filters).forEach(key => {
            const values = filters[key];
            if (this.isValidFilterValue(values)) {
                url = this.appendFilterValue(url, key, values);
            }
        });
        return url;
    }

    /**
     * Verifica si el valor del filtro es válido
     */
    private isValidFilterValue(value: any): boolean {
        return value !== null && value !== undefined;
    }

    /**
     * Agrega un valor de filtro a la URL
     */
    private appendFilterValue(url: string, key: string, value: number[] | string): string {
        if (Array.isArray(value)) {
            return this.appendArrayFilter(url, key, value);
        }
        return this.appendStringFilter(url, key, value);
    }

    /**
     * Agrega un filtro de tipo array a la URL
     */
    private appendArrayFilter(url: string, key: string, values: number[]): string {
        if (values.length === 0) {
            return url;
        }
        
        values.forEach(value => {
            url += `&${key}=${encodeURIComponent(value)}`;
        });
        return url;
    }

    /**
     * Agrega un filtro de tipo string a la URL
     */
    private appendStringFilter(url: string, key: string, value: string): string {
        const stringValue = String(value).trim();
        if (stringValue.length === 0) {
            return url;
        }
        return url + `&${key}=${encodeURIComponent(stringValue)}`;
    }

    // Obtener las bases de conocimiento asociadas al usuario actual
    getKnowledgeBasesByUser(): Observable<KnowledgeBase[]> {        
        if (environment.mockAuth) {
            return this.getMockKnowledgeBases();
        }

        const headers = this.authService.getAuthHeaders();

        return this.http.get<KnowledgeBaseResponse>(this.knowledgeBaseEndpoint, { headers }).pipe(
            map(response => response.knowledgeBases),
            catchError(error => {
                return throwError(() => error);
            })
        );
    }

    getKnowledgeBaseById(kbId: number | string): Observable<KnowledgeBase | null> {
        if (environment.mockAuth) {
            return this.getMockKnowledgeBases().pipe(
                map(kbs => {
                    const id = typeof kbId === 'string' ? kbId : String(kbId);
                    return kbs.find(kb => kb.code === id || String(kb.knowledgeBaseId) === id) || null;
                })
            );
        }

        const headers = this.authService.getAuthHeaders();
        const url = `${this.knowledgeBaseEndpoint}/${kbId}`;

        return this.http.get<SingleKnowledgeBaseResponse>(url, { headers }).pipe(
            map(response => response.knowledgeBase),
            catchError(error => {
                console.error('Error fetching knowledge base by ID:', error);
                return of(null);
            })
        );
    }

    getKnowledgeBaseByCode(code: string): Observable<KnowledgeBase | null> {
        return this.getKnowledgeBaseById(code);
    }

    getArticleHistory(articleId: number): Observable<ArticleHistoryResponse> {
        if (environment.mockAuth) {
            //TODO: Implementar datos de prueba para el historial de artículos
        }
        const headers = this.authService.getAuthHeaders();
        let url = `${this.knowledgeBaseEndpoint}/articles/${articleId}/track`;

        return this.http.get<ArticleHistoryResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error fetching article history:', error);
                return throwError(() => error);
            })
        );
    }

    getArticleByVersion(
        kbId: number,
        articleId: number,
        environment: 'DRAFT' | 'QA' | 'PROD',
        version?: number
    ): Observable<{article: ArticleListItem, track: ArticleHistoryResponse}> {
        const headers = this.authService.getAuthHeaders();
        const url = this.buildArticleVersionUrl(kbId, articleId, environment, version);

        return this.http.get<{article: any, track: any}>(url, { headers }).pipe(
            map(response => {
                const article = this.mapApiResponseToArticle(response, environment);
                return { article, track: response.track };
            }),
            catchError(error => {
                console.error('Error fetching article by version:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Construye la URL para obtener un artículo por versión
     */
    private buildArticleVersionUrl(
        kbId: number,
        articleId: number,
        environment: string,
        version?: number
    ): string {
        let url = `${this.knowledgeBaseEndpoint}/${kbId}/articles/${articleId}?environment=${environment}`;
        
        if (version !== undefined && version !== null) {
            url += `&version=${version}`;
        }
        
        return url;
    }

    /**
     * Mapea la respuesta del API al formato ArticleListItem
     */
    private mapApiResponseToArticle(
        response: any,
        environment: 'DRAFT' | 'QA' | 'PROD'
    ): ArticleListItem {
        const article = response.article;
        const track = response.track;

        return {
            ...this.mapArticleBaseFields(article, track, environment),
            ...this.mapArticlePicklistFields(article),
            ...this.mapArticleDateFields(article),
            ...this.mapArticleTextFields(article),
            ...this.mapArticleMetadata(article, track)
        } as ArticleListItem;
    }

    /**
     * Mapea campos base del artículo
     */
    private mapArticleBaseFields(article: any, track: any, environment: string): Partial<ArticleListItem> {
        return {
            knowledgeArticleId: article.knowledgeArticleId,
            knowledgeBaseId: article.knowledgeBaseId,
            knowledgeArticleRevisionId: article.knowledgeArticleRevisionId || undefined,
            identifier: track.identifier,
            state: environment as ArticleState,
            revisionId: article.knowledgeArticleRevisionId || null,
            versionNumber: article.versionNumber || null,
            isActive: article.isActive
        };
    }

    /**
     * Mapea campos picklist del artículo
     */
    private mapArticlePicklistFields(article: any): Partial<ArticleListItem> {
        return {
            category1PicklistItemId: article.category1PicklistItemId || undefined,
            category2PicklistItemId: article.category2PicklistItemId || undefined,
            category1SalesforcePicklistItemId: article.category1SalesforcePicklistItemId || undefined,
            category2SalesforcePicklistItemId: article.category2SalesforcePicklistItemId || undefined,
            actionsPicklistItemId: article.actionsPicklistItemId || undefined,
            requestTypePicklistItemId: article.requestTypePicklistItemId || undefined,
            businessPicklistItemId: article.businessPicklistItemId || undefined
        };
    }

    /**
     * Mapea campos de fecha del artículo
     */
    private mapArticleDateFields(article: any): Partial<ArticleListItem> {
        return {
            startDate: article.startDate || undefined,
            endDate: article.endDate || undefined
        };
    }

    /**
     * Mapea campos de texto del artículo
     */
    private mapArticleTextFields(article: any): Partial<ArticleListItem> {
        return {
            question: article.question || '',
            content: article.content || '',
            keywords: article.keywords || undefined,
            protocol: article.protocol || undefined,
            executiveVoiceProtocol: article.executiveVoiceProtocol || undefined,
            comments: article.comments || undefined,
            technicalComment: article.technicalComment || undefined,
            changeSummary: article.changeSummary || undefined
        };
    }

    /**
     * Mapea campos de metadata del artículo
     */
    private mapArticleMetadata(article: any, track: any): Partial<ArticleListItem> {
        return {
            lastModifiedBy: track.draftUpdatedBy || 0,
            lastModifiedByEmail: article.publishedBy || track.draftUpdatedByEmail || undefined,
            lastModifiedAt: article.publishedAt || track.draftUpdatedAt || new Date().toISOString(),
            statusCode: article.statusCode || undefined
        };
    }

    createArticle(knowledgeBaseId: number, articleData: Partial<ArticleListItem>): Observable<any> {
        if (environment.mockAuth) {
            //TODO: Implementar creación de artículo en modo mock
        }

        const headers = this.authService.getAuthHeaders();
        const url = `${this.knowledgeBaseEndpoint}/articles/batch-draft`;
        
        // Campos obligatorios
        const draft: any = {
            identifier: articleData.identifier,
            question: articleData.question,
            content: articleData.content
        };
        
        // Agregar campos opcionales de forma dinámica
        this.addOptionalFields(draft, articleData, [
            'category1PicklistItemId',
            'category2SalesforcePicklistItemId',
            'category2PicklistItemId',
            'category1SalesforcePicklistItemId',
            'actionsPicklistItemId',
            'requestTypePicklistItemId',
            'businessPicklistItemId',
            'keywords',
            'protocol',
            'executiveVoiceProtocol',
            'comments',
            'technicalComment',
            'startDate',
            'endDate',
            'isActive'
        ]);
        
        const payload = {
            kbId: knowledgeBaseId,
            drafts: [draft]
        };

        return this.http.post<BatchCreateDraftsResponse>(url, payload, { headers }).pipe(
            map(response => response),
            catchError(error => {
                console.error('Error creating article draft:', error);
                return throwError(() => error);
            })
        );
    }

    updateArticle(articleId: number, articleData: Partial<ArticleListItem>): Observable<any> {
        if (environment.mockAuth) {
            //TODO: Implementar edición de artículo en modo mock
        }

        const headers = this.authService.getAuthHeaders();
        const url = `${this.knowledgeBaseEndpoint}/articles/batch-draft`;
        
        // Inicializar objeto vacío para campos opcionales
        const draftData: any = {};
        
        // Agregar campos opcionales de forma dinámica (incluir undefined para detectar cambios)
        this.addOptionalFieldsStrict(draftData, articleData, [
            'identifier',
            'question',
            'content',
            'category1PicklistItemId',
            'category2SalesforcePicklistItemId',
            'category2PicklistItemId',
            'category1SalesforcePicklistItemId',
            'actionsPicklistItemId',
            'requestTypePicklistItemId',
            'businessPicklistItemId',
            'keywords',
            'protocol',
            'executiveVoiceProtocol',
            'comments',
            'technicalComment',
            'startDate',
            'endDate',
            'isActive'
        ]);
        
        const payload = {
            updates: [
                {
                    articleId: articleId,
                    draftData: draftData
                }
            ]
        };

        return this.http.put<BatchCreateDraftsResponse>(url, payload, { headers }).pipe(
            map(response => response),
            catchError(error => {
                console.error('Error updating article draft:', error);
                return throwError(() => error);
            })
        );
    }

    publishArticles(articleIds: number[], state: string): Observable<any> {
        if (environment.mockAuth) {
            //TODO: Implementar publicación de artículos en modo mock
        }
        const headers = this.authService.getAuthHeaders();
        const url = `${this.knowledgeBaseEndpoint}/articles/batch-publish-${state === 'DRAFT' ? 'qa' : 'prod'}`;
        
        let payload = {}
        if (state === 'DRAFT') {
            payload = {
            articleIds: articleIds,
            changeSummary: `Published by ${this.currentUsername} via Knowledge Base Service`
            };
        } else if (state === 'QA') {
            payload = {
            qaRevisionIds: articleIds,
            changeSummary: `Published by ${this.currentUsername} via Knowledge Base Service`
            };
        }
        return this.http.post<BatchCreateDraftsResponse>(url, payload, { headers }).pipe(
            map(response => response),
            catchError(error => {
                console.error('Error publishing articles:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Upload articles from Excel file (async job)
     */
    uploadArticlesFromExcel(knowledgeBaseId: number, file: File): Observable<ArticleImportJobInitResponse> {
        if (environment.mockAuth) {
            return of({
                jobId: 'mock-job-id-' + Date.now(),
                statusPath: `/knowledge-base/${knowledgeBaseId}/articles/upload/jobs/mock-job-id`
            } as ArticleImportJobInitResponse);
        }
        const headers = this.authService.getAuthHeaders()
            .set('Content-Type', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/articles/upload`;

        return this.http.post<ArticleImportJobInitResponse>(url, file, { headers }).pipe(
            catchError(error => {
                console.error('Error uploading articles:', error);
                return throwError(() => error);
            })
        );
    }

    exportArticlesToExcel(
        knowledgeBaseId: number,
        status?: 'DRAFT' | 'QA' | 'PROD'
    ): Observable<ImportJobDownloadResponse> {
        if (environment.mockAuth) {
            return of({
                downloadUrl: 'https://example.com/mock-export.xlsx',
                expiresIn: 60
            });
        }

        const headers = this.authService.getAuthHeaders();
        let url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/articles/export`;
        if (status) {
            url += `?status=${encodeURIComponent(status)}`;
        }

        return this.http.get<ImportJobDownloadResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error exporting knowledge base articles:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Get the status of an async article import job
     */
    listImportJobs(
        knowledgeBaseId: number,
        page: number = 1,
        pageSize: number = 20,
        status?: string,
        date?: string
    ): Observable<ImportJobsListResponse> {
        if (environment.mockAuth) {
            return of({
                items: [],
                pagination: { currentPage: 1, pageSize, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false }
            } as ImportJobsListResponse);
        }
        const headers = this.authService.getAuthHeaders();
        let url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/articles/upload/jobs?page=${page}&pageSize=${pageSize}`;
        if (status) url += `&status=${status}`;
        if (date) url += `&date=${date}`;
        return this.http.get<ImportJobsListResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error fetching import jobs:', error);
                return throwError(() => error);
            })
        );
    }

    getImportJobStatus(knowledgeBaseId: number, jobId: string): Observable<ArticleImportJobStatusResponse> {
        if (environment.mockAuth) {
            return of({
                jobId,
                kbId: knowledgeBaseId,
                userId: 1,
                status: 'COMPLETED' as const,
                s3Key: 'mock/key.xlsx',
                totalRows: 10,
                processedRows: 10,
                result: {
                    success: true,
                    total_rows: 10,
                    validated_rows: 10,
                    created_drafts: 8,
                    updated_drafts: 2,
                    errors: [],
                    warnings: [],
                    details: null
                },
                errorMessage: null,
                resultAvailable: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            } as ArticleImportJobStatusResponse);
        }
        const headers = this.authService.getAuthHeaders();
        const url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/articles/upload/jobs/${jobId}`;
        return this.http.get<ArticleImportJobStatusResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error fetching import job status:', error);
                return throwError(() => error);
            })
        );
    }

    downloadImportJobResult(knowledgeBaseId: number, jobId: string): Observable<ImportJobDownloadResponse> {
        if (environment.mockAuth) {
            return of({ downloadUrl: 'https://example.com/mock-result.xlsx', expiresIn: 300 });
        }
        const headers = this.authService.getAuthHeaders();
        const url = `${this.knowledgeBaseEndpoint}/${knowledgeBaseId}/articles/upload/jobs/${jobId}/download`;
        return this.http.get<ImportJobDownloadResponse>(url, { headers }).pipe(
            catchError(error => {
                console.error('Error downloading import job result:', error);
                return throwError(() => error);
            })
        );
    }

    private getMockArticles(): Observable<ArticleListItem[]> {
        // Datos de prueba para ver la funcionalidad
        const mockArticles: ArticleListItem[] = [
            {
                knowledgeArticleId: 1,
                knowledgeBaseId: 1,
                identifier: 'ART001',
                state: 'PROD',
                revisionId: 10,
                versionNumber: 1,
                category1PicklistItemId: 1,
                // Opcionales como undefined si no hay valor
                category2PicklistItemId: undefined,
                category1SalesforcePicklistItemId: undefined,
                category2SalesforcePicklistItemId: undefined,
                actionsPicklistItemId: 1,
                requestTypePicklistItemId: undefined,
                businessPicklistItemId: undefined,
                isActive: true,
                startDate: '2026-01-01T09:00:00Z',
                endDate: undefined,
                question: '¿Cómo resetear mi contraseña?',
                content: 'Para resetear tu contraseña, ve a la página de login y haz clic en "Olvidé mi contraseña".',
                keywords: 'contraseña,resetear,login',
                protocol: 'AUTH-001',
                executiveVoiceProtocol: undefined,
                comments: undefined,
                technicalComment: undefined,
                changeSummary: 'Creación inicial',
                lastModifiedBy: 100,
                lastModifiedAt: '2026-01-27T10:00:00Z',
            },
            {
                knowledgeArticleId: 2,
                knowledgeBaseId: 1,
                identifier: 'ART002',
                state: 'PROD',
                revisionId: 11,
                versionNumber: 1,
                category1PicklistItemId: 2,
                category2PicklistItemId: undefined,
                category1SalesforcePicklistItemId: undefined,
                category2SalesforcePicklistItemId: undefined,
                actionsPicklistItemId: 2,
                requestTypePicklistItemId: undefined,
                businessPicklistItemId: undefined,
                isActive: true,
                startDate: '2026-01-01T09:00:00Z',
                endDate: undefined,
                question: '¿Cómo contactar soporte técnico?',
                content: 'Puedes contactar soporte técnico a través del formulario de contacto o llamando al 123-456-7890.',
                keywords: 'soporte,contacto,ayuda',
                protocol: 'SUPPORT-002',
                executiveVoiceProtocol: undefined,
                comments: undefined,
                technicalComment: undefined,
                changeSummary: 'Creación inicial',
                lastModifiedBy: 100,
                lastModifiedAt: '2026-01-27T10:00:00Z',
            },
            {
                knowledgeArticleId: 3,
                knowledgeBaseId: 1,
                identifier: 'ART003',
                state: 'PROD',
                revisionId: 12,
                versionNumber: 1,
                category1PicklistItemId: 1,
                category2PicklistItemId: undefined,
                category1SalesforcePicklistItemId: undefined,
                category2SalesforcePicklistItemId: undefined,
                actionsPicklistItemId: 1,
                requestTypePicklistItemId: undefined,
                businessPicklistItemId: undefined,
                isActive: true,
                startDate: '2026-01-01T09:00:00Z',
                endDate: undefined,
                question: '¿Cuál es el horario de atención?',
                content: 'Nuestro horario de atención es de lunes a viernes de 9:00 AM a 6:00 PM.',
                keywords: 'horario,atención,tiempo',
                protocol: 'INFO-003',
                executiveVoiceProtocol: undefined,
                comments: undefined,
                technicalComment: undefined,
                changeSummary: 'Creación inicial',
                lastModifiedBy: 100,
                lastModifiedAt: '2026-01-27T10:00:00Z',
            },
        ];
        return of(mockArticles);
    }

    private getMockKnowledgeBases(): Observable<KnowledgeBase[]> {
        return of(MOCK_KNOWLEDGE_BASES);
    }

    private getMockArticlesResponse(
        knowledgeBaseId: number, 
        state?: string, 
        page: number = 1, 
        pageSize: number = 20
    ): Observable<ListArticlesResponse> {
        return this.getMockArticles().pipe(
            map(allArticles => {
                // Filter by knowledgeBaseId and state
                let filteredArticles = allArticles.filter(article => {
                    const matchesKb = article.knowledgeBaseId === knowledgeBaseId;
                    const matchesState = !state || article.state === state;
                    return matchesKb && matchesState;
                });

                // Calculate pagination
                const totalItems = filteredArticles.length;
                const totalPages = Math.ceil(totalItems / pageSize);
                const startIndex = (page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const paginatedArticles = filteredArticles.slice(startIndex, endIndex);

                return {
                    articles: paginatedArticles,
                    pagination: {
                        currentPage: page,
                        pageSize: pageSize,
                        totalItems: totalItems,
                        totalPages: totalPages,
                        hasNextPage: page < totalPages,
                        hasPreviousPage: page > 1
                    },
                    status: state
                };
            })
        );
    }

    /**
     * Agrega campos opcionales al objeto si existen y no son null/undefined/vacíos
     * @param target - Objeto destino donde agregar los campos
     * @param source - Objeto fuente con los datos
     * @param fieldNames - Nombres de los campos a procesar
     */
    private addOptionalFields(
        target: any,
        source: Partial<ArticleListItem>,
        fieldNames: (keyof ArticleListItem)[]
    ): void {
        fieldNames.forEach(field => {
            const value = source[field];
            
            // No agregar si es undefined, null o string vacío
            if (value === undefined || value === null || value === '') {
                return;
            }
            
            target[field] = value;
        });
    }

    /**
     * Agrega campos opcionales al objeto solo si están definidos (para actualización)
     * No agrega null, undefined ni strings vacíos
     * @param target - Objeto destino donde agregar los campos
     * @param source - Objeto fuente con los datos
     * @param fieldNames - Nombres de los campos a procesar
     */
    private addOptionalFieldsStrict(
        target: any,
        source: Partial<ArticleListItem>,
        fieldNames: (keyof ArticleListItem)[]
    ): void {
        fieldNames.forEach(field => {
            if (field in source && source[field] !== undefined) {
                const value = source[field];
                
                // No agregar null, undefined ni strings vacíos
                if (value === null || value === undefined || value === '') {
                    return;
                }
                
                target[field] = value;
            }
        });
    }

    /**
     * Ejecuta eliminación lógica inmediata en todos los ambientes para los artículos enviados.
     * Desactiva draft.is_active=false, crea revisiones QA y PROD inactivas y dispara
     * sincronización asíncrona a Bedrock en ambos ambientes.
     * Retorna 200 si todos exitosos, 207 Multi-Status si hay mezcla de éxitos y fallos.
     */
    /**
     * Prueba el retrieval de artículos para una pregunta dada en un ambiente específico.
     * POST /knowledge-base/{kbid}/retrieval
     */
    testRetrieval(kbId: number, question: string, environmentCode: 'QA' | 'PROD'): Observable<RetrievalResponse> {
        if (environment.mockAuth) {
            return of({
                data: {
                    question,
                    proposedAction: null,
                    retrievedArticles: [],
                    rerankedArticles: [],
                    summary: { totalRetrieved: 0, totalReranked: 0, topK: 30, rerankTopN: 10 }
                }
            });
        }

        const headers = this.authService.getAuthHeaders();
        const url = `${this.knowledgeBaseEndpoint}/${kbId}/retrieval`;
        const payload = { question, environment: environmentCode };

        return this.http.post<RetrievalResponse>(url, payload, { headers }).pipe(
            catchError(error => {
                console.error('Error testing retrieval:', error);
                return throwError(() => error);
            })
        );
    }

    deleteArticles(articleIds: number[], changeSummary?: string): Observable<BatchDeleteImmediateResponse> {
        if (environment.mockAuth) {
            return of({
                successful: articleIds.map(id => ({
                    articleId: id,
                    qaRevisionId: 0,
                    prodRevisionId: 0,
                    qaSyncId: 0,
                    prodSyncId: 0
                })),
                failed: [],
                summary: { total: articleIds.length, succeeded: articleIds.length, failed: 0 },
                qaSyncId: 0,
                prodSyncId: 0
            });
        }

        const headers = this.authService.getAuthHeaders();
        const url = `${this.knowledgeBaseEndpoint}/articles/batch-delete-immediate`;
        const payload = {
            articleIds,
            changeSummary: changeSummary ?? `Baja inmediata por ${this.currentUsername}`
        };

        return this.http.post<BatchDeleteImmediateResponse>(url, payload, { headers }).pipe(
            catchError(error => {
                console.error('Error eliminando artículos:', error);
                return throwError(() => error);
            })
        );
    }
}
